import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { CoreService } from '../../../services/core.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SidebarComponent } from '../../sidebars/sidebar/sidebar.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: 'app-social-ai-list-of-bulk-posts-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    MatTooltipModule,
    SidebarComponent
  ],
  templateUrl: './social-ai-list-of-bulk-posts-table.component.html',
  styleUrl: './social-ai-list-of-bulk-posts-table.component.scss'
})
export class SocialAiListOfBulkPostsTableComponent implements OnInit {

  dataSource = new MatTableDataSource<any>([]);
  expandedElement: any | null = null;
  expandedErrorLogsElement: any | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  companyID: number = 0;
  totalItems = 0;
  pageSize = 12;

  deletingBulkId: number | null = null;
  currentPage = 1;

  weekDays: string[] = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  constructor(
    private coreService: CoreService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const page = params['page'] ? Number(params['page']) : 1;
      this.currentPage = page;
      this.loadData(page);
    });

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  loadData(page: number) {
    this.companyID = this.coreService.user?.companyID || 0;
    this.currentPage = page;

    this.coreService
      .postData('bulks/index', { company_id: this.companyID, page })
      .subscribe((res: any) => {
        const data = (res.data || []).map((item: any) => ({
          ...item,
          failed_posts: item.failed_posts?.map((fp: any) => ({
            ...fp,
            error_messages: this.safeParse(fp.error_messages)
          })) || []
        }));

        this.dataSource.data = data;
        this.totalItems = res.total;
        this.pageSize = res.per_page;

        if (this.paginator) {
          this.paginator.pageIndex = res.current_page - 1;
          this.paginator.length = res.total;
          this.paginator.pageSize = res.per_page;
        }
      });
  }

  deleteBulkUpload(element: any): void {
    if (!element?.id) {
      alert('Bulk upload ID was not found.');
      return;
    }

    const confirmed = confirm(`Are you sure you want to delete "${element.file_name}"?`);

    if (!confirmed) {
      return;
    }

    this.companyID = this.coreService.user?.companyID || 0;
    this.deletingBulkId = element.id;

    const payload = {
      id: element.id,
      company_id: this.companyID
    };

    // TODO backend: φτιάξε route POST 'bulks/delete' στο BulkPostController για διαγραφή bulk upload από τη βάση
    // Στο routes/api.php βάλε:
    // Route::post('bulks/delete', [BulkPostController::class, 'delete'])->name('bulk.delete');
    this.coreService.postData('bulks/delete', payload).subscribe({
      next: () => {
        this.dataSource.data = this.dataSource.data.filter(
          item => item.id !== element.id
        );

        this.totalItems = Math.max(this.totalItems - 1, 0);

        if (this.expandedElement === element) {
          this.expandedElement = null;
        }

        if (this.expandedErrorLogsElement === element) {
          this.expandedErrorLogsElement = null;
        }

        this.deletingBulkId = null;

        if (this.dataSource.data.length === 0 && this.currentPage > 1) {
          this.loadData(this.currentPage - 1);
          return;
        }

        this.loadData(this.currentPage);
      },
      error: (error) => {
        console.error('Failed to delete bulk upload:', error);
        this.deletingBulkId = null;
        alert('Could not delete bulk upload. Please try again.');
      }
    });
  }

  private safeParse(value: any) {
    try {
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      return { message: value };
    }
  }

  onPageChange(event: any) {
    const newPage = event.pageIndex + 1;
    this.currentPage = newPage;
    this.loadData(newPage);
  }

  editRow(row: any) {
    this.router.navigate(['/manual-post/edit', row.id]);
  }

  createBulkPost() {
    this.router.navigate(['/bulk-upload']);
  }

  // Sidebar helpers
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Post[] | null;
        this.posts = Array.isArray(parsed) ? parsed.slice(0, this.maxSidebarCards) : [];
      } catch (e) {
        console.error('Failed to parse latest posts from localStorage', e);
        this.posts = [];
      }
    }

    this.coreService.latestPosts$.subscribe(posts => {
      if (posts && posts.length > 0) {
        this.posts = posts.slice(0, this.maxSidebarCards);
      }
    });
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this.alerts = parsed.slice(0, this.maxSidebarCards);
        }
      } catch (e) {
        console.error('Failed to parse alerts from storage', e);
      }
    }

    this.coreService.latestAlerts$.subscribe(alerts => {
      if (alerts && alerts.length > 0) {
        this.alerts = alerts.slice(0, this.maxSidebarCards);
      }
    });
  }
}